import React, { Component } from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import {
  CssBaseline, Container, Typography, Button, Chip, Skeleton, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete, CircularProgress,
  Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { Styles } from '../../styles/material-styles';
import CustomizedSnackbars from '../../components/material-snackbars';
import HeaderMenu from '../../components/header-menu';
import Api from '../../services/api';
import ProviderGroupService, { normalizeGroup, normalizeAgent, normalizeProvider, normalizeChannel, normalizeNumber, normalizeTrace } from '../../services/providergroup.service';
import { injectIntl } from 'react-intl';
import * as componentActions from '../../redux/actions/header-menu';
import GroupsIcon from '@mui/icons-material/Groups';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';

// Status da operacao de CREATE (tb_2talk_provider_group_operation.status)
const OP_PENDING = 1;
const OP_CONFIRMED = 2;
const OP_FAILED = 3;

const pageStyles = {
  contentWrapper: { padding: '24px 28px', width: '100%' },
  heading: { fontSize: 24, fontWeight: 600, margin: 0, color: '#263238' },
  subheading: { fontSize: 14, color: '#888', marginTop: 6, marginBottom: 20 },
  toolbar: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 },
  emptyCard: {
    border: '1px solid #e0e0e0', borderRadius: 12, padding: '48px 24px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', background: '#fff',
  },
  emptyTitle: { fontSize: 18, fontWeight: 600, color: '#455a64', marginTop: 12 },
  emptyDesc: { fontSize: 14, color: '#888', marginTop: 6, marginBottom: 18 },
  link: { fontFamily: 'monospace', fontSize: 12, color: '#277afe', wordBreak: 'break-all' },
  mono: { fontFamily: 'monospace', fontSize: 12, color: '#555', wordBreak: 'break-all' },
};

class ProviderGroupList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      actionLoading: false,
      groups: [],
      users: [],
      openNotification: false,
      notificationVariant: 'error',
      notificationMessage: '',
      modalOpen: false,
      formName: '',
      formDescription: '',
      formJoinMode: 'auto_approve',
      formAgents: [],
      // Cascata GENERICA: provedor -> canais do provedor -> numeros ATIVOS do canal
      providers: [],
      channels: [],
      numbers: [],
      loadingChannels: false,
      loadingNumbers: false,
      formProvider: null,
      formChannel: null,
      formNumber: null,
      // Modo edicao (reusa o mesmo modal). editConfirmed = grupo ja existe na Meta.
      editMode: false,
      editChatId: null,
      editConfirmed: false,
      // Menu 3-pontinhos por linha (anchor + grupo alvo).
      menuAnchor: null,
      menuGroup: null,
      // Modal de erro/diagnostico
      errorModalOpen: false,
      errorGroup: null,
      trace: [],
      loadingTrace: false,
    };
    this.closeNotification = this.closeNotification.bind(this);
    this.pollTimer = null;
  }

  componentDidMount() {
    this.loadData(true);
    this.loadUsers();
    // Auto-refresh: enquanto houver grupo pendente, atualiza pra ver o webhook confirmar.
    this.pollTimer = setInterval(() => {
      const hasPending = (this.state.groups || []).some(g => g.operationStatus === OP_PENDING);
      if (hasPending && !this.state.loading) this.loadData(false);
    }, 8000);
  }

  componentWillUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  }

  // ATENCAO: arrow class field NAO pode ser async (babel 7.12.3 quebra) -> Promise chain.
  loadData = (showSkeleton) => {
    if (showSkeleton) this.setState({ loading: true });
    ProviderGroupService.getGroups()
      .then(result => {
        const list = result.data && result.data.success ? (result.data.data || []).map(normalizeGroup) : [];
        this.setState({ loading: false, groups: list });
      })
      .catch(err => {
        this.setState({
          loading: false, openNotification: true, notificationVariant: 'error',
          notificationMessage: err.response?.data?.errors?.[0] || this.props.intl.formatMessage({ id: 'providergroup.error.loading' }),
        });
        // API fora: o client axios pode nao estar inicializado (null) — nao pode crashar a tela.
        if (Api && typeof Api.kickoff === 'function') Api.kickoff(err);
      });
  }

  // Agentes elegiveis (tecnicos, nao-somente-cliente) — filtro feito no backend do 2TALK.
  loadUsers = () => {
    ProviderGroupService.getAgents()
      .then(result => {
        const list = result.data && result.data.success ? (result.data.data || []).map(normalizeAgent) : [];
        this.setState({ users: list });
      })
      .catch(() => this.setState({ users: [] }));
  }

  openModal = () => {
    this.setState({
      modalOpen: true, editMode: false, editChatId: null, editConfirmed: false,
      formName: '', formDescription: '', formJoinMode: 'auto_approve', formAgents: [],
      formProvider: null, formChannel: null, formNumber: null, channels: [], numbers: [],
    });
    this.loadProviders();
  }

  // Edicao: pre-preenche o form. Provedor/canal/numero NAO sao editaveis (grupo ja atrelado);
  // por isso ficam ocultos no modo edicao.
  openEdit = (group) => {
    const users = this.state.users || [];
    const ids = group.agentUserIds || [];
    const selectedAgents = users.filter(u => ids.indexOf(u.id) > -1);
    this.setState({
      modalOpen: true, editMode: true, editChatId: group.chatId,
      editConfirmed: !!group.groupId, // ja existe na Meta
      formName: group.groupName || '',
      formDescription: group.groupDescription || '',
      formJoinMode: group.joinApprovalMode || 'auto_approve',
      formAgents: selectedAgents,
      formProvider: null, formChannel: null, formNumber: null,
    });
  }

  // Modal de erro: op.errorMessage (ja vem no grid) + rastro do fluxo (tb_integration_event_trace).
  openErrorModal = (group) => {
    this.setState({ errorModalOpen: true, errorGroup: group, trace: [], loadingTrace: true });
    ProviderGroupService.getTrace(group.chatId)
      .then(result => {
        const list = result.data && result.data.success ? (result.data.data || []).map(normalizeTrace) : [];
        this.setState({ loadingTrace: false, trace: list });
      })
      .catch(() => this.setState({ loadingTrace: false, trace: [] }));
  }

  closeErrorModal = () => {
    this.setState({ errorModalOpen: false, errorGroup: null, trace: [] });
  }

  openRowMenu = (event, group) => {
    this.setState({ menuAnchor: event.currentTarget, menuGroup: group });
  }

  closeRowMenu = () => {
    this.setState({ menuAnchor: null, menuGroup: null });
  }

  handleRetry = (group) => {
    const intl = this.props.intl;
    this.setState({ actionLoading: true });
    ProviderGroupService.retryGroup(group.chatId)
      .then(result => {
        const ok = result.data && result.data.success;
        this.setState({
          actionLoading: false, openNotification: true,
          notificationVariant: ok ? 'success' : 'error',
          notificationMessage: ok
            ? intl.formatMessage({ id: 'providergroup.success.retried' })
            : ((result.data && result.data.errors && result.data.errors[0]) || intl.formatMessage({ id: 'providergroup.error.retrying' })),
        });
        if (ok) this.loadData(false);
      })
      .catch(err => {
        this.setState({
          actionLoading: false, openNotification: true, notificationVariant: 'error',
          notificationMessage: err.response?.data?.errors?.[0] || intl.formatMessage({ id: 'providergroup.error.retrying' }),
        });
        if (Api && typeof Api.kickoff === 'function') Api.kickoff(err);
      });
  }

  // Reset do invite link (op sincrona no backend): dispara e recarrega quando confirmar.
  handleResetInvite = (group) => {
    const intl = this.props.intl;
    this.setState({ actionLoading: true });
    ProviderGroupService.resetInviteLink(group.chatId)
      .then(result => {
        const ok = result.data && result.data.success;
        this.setState({
          actionLoading: false, openNotification: true,
          notificationVariant: ok ? 'success' : 'error',
          notificationMessage: ok
            ? intl.formatMessage({ id: 'providergroup.success.invitereset' })
            : ((result.data && result.data.errors && result.data.errors[0]) || intl.formatMessage({ id: 'providergroup.error.invitereset' })),
        });
        if (ok) this.loadData(false);
      })
      .catch(err => {
        this.setState({
          actionLoading: false, openNotification: true, notificationVariant: 'error',
          notificationMessage: err.response?.data?.errors?.[0] || intl.formatMessage({ id: 'providergroup.error.invitereset' }),
        });
        if (Api && typeof Api.kickoff === 'function') Api.kickoff(err);
      });
  }

  // 1o passo da cascata: provedores de mensageria (catalogo tb_2talk_message_provider).
  loadProviders = () => {
    ProviderGroupService.getProviders()
      .then(result => {
        const list = result.data && result.data.success ? (result.data.data || []).map(normalizeProvider) : [];
        this.setState({ providers: list });
        // Se so houver um provedor, ja seleciona e desce a cascata.
        if (list.length === 1) this.handleProviderChange(list[0]);
      })
      .catch(() => this.setState({ providers: [] }));
  }

  // Cascata: provedor -> canais DAQUELE provedor (sem provider hardcoded).
  handleProviderChange = (provider) => {
    this.setState({ formProvider: provider, formChannel: null, formNumber: null, channels: [], numbers: [] });
    if (!provider) return;
    this.setState({ loadingChannels: true });
    ProviderGroupService.getChannelsByProvider(provider.id)
      .then(result => {
        const list = result.data && result.data.success ? (result.data.data || []).map(normalizeChannel) : [];
        this.setState({ loadingChannels: false, channels: list });
        if (list.length === 1) this.handleChannelChange(list[0]);
      })
      .catch(() => this.setState({ loadingChannels: false, channels: [] }));
  }

  // Cascata: ao escolher o canal, carrega os numeros ATIVOS dele.
  handleChannelChange = (channel) => {
    this.setState({ formChannel: channel, formNumber: null, numbers: [] });
    if (!channel) return;
    this.setState({ loadingNumbers: true });
    ProviderGroupService.getChannelNumbers(channel.id)
      .then(result => {
        const list = result.data && result.data.success ? (result.data.data || []).map(normalizeNumber) : [];
        // Se houver só um numero ativo, ja seleciona (menos clique pro operador).
        this.setState({ loadingNumbers: false, numbers: list, formNumber: list.length === 1 ? list[0] : null });
      })
      .catch(() => this.setState({ loadingNumbers: false, numbers: [] }));
  }

  closeModal = () => {
    if (!this.state.actionLoading) this.setState({ modalOpen: false });
  }

  handleCreate = () => {
    const intl = this.props.intl;
    const { editMode } = this.state;

    if (!this.state.formName || this.state.formName.trim().length === 0) {
      this.setState({
        openNotification: true, notificationVariant: 'error',
        notificationMessage: intl.formatMessage({ id: 'providergroup.error.namerequired' }),
      });
      return;
    }

    // Provedor/canal/numero só são exigidos na CRIACAO (na edicao o grupo ja esta atrelado).
    if (!editMode) {
      if (!this.state.formProvider) {
        this.setState({ openNotification: true, notificationVariant: 'error', notificationMessage: intl.formatMessage({ id: 'providergroup.error.providerrequired' }) });
        return;
      }
      if (!this.state.formChannel) {
        this.setState({ openNotification: true, notificationVariant: 'error', notificationMessage: intl.formatMessage({ id: 'providergroup.error.channelrequired' }) });
        return;
      }
      if (!this.state.formNumber) {
        this.setState({ openNotification: true, notificationVariant: 'error', notificationMessage: intl.formatMessage({ id: 'providergroup.error.numberrequired' }) });
        return;
      }
    }

    this.setState({ actionLoading: true });
    const agentuserids = (this.state.formAgents || []).map(u => u.id);

    const onOk = (successKey) => {
      this.setState({
        actionLoading: false, modalOpen: false, openNotification: true, notificationVariant: 'success',
        notificationMessage: intl.formatMessage({ id: successKey }),
      });
      this.loadData(false);
    };
    const onFail = (result, errKey) => {
      this.setState({
        actionLoading: false, openNotification: true, notificationVariant: 'error',
        notificationMessage: (result && result.data && result.data.errors && result.data.errors[0]) || intl.formatMessage({ id: errKey }),
      });
    };
    const onError = (err, errKey) => {
      this.setState({
        actionLoading: false, openNotification: true, notificationVariant: 'error',
        notificationMessage: err.response?.data?.errors?.[0] || intl.formatMessage({ id: errKey }),
      });
      if (Api && typeof Api.kickoff === 'function') Api.kickoff(err);
    };

    if (editMode) {
      const payload = {
        subject: this.state.formName.trim(),
        description: this.state.formDescription || null,
        joinapprovalmode: this.state.formJoinMode,
        agentuserids,
      };
      ProviderGroupService.updateGroup(this.state.editChatId, payload)
        .then(result => (result.data && result.data.success) ? onOk('providergroup.success.updated') : onFail(result, 'providergroup.error.updating'))
        .catch(err => onError(err, 'providergroup.error.updating'));
    } else {
      const payload = {
        subject: this.state.formName.trim(),
        description: this.state.formDescription || null,
        joinapprovalmode: this.state.formJoinMode,
        channelid: this.state.formChannel.id,
        registrationnumberrequestid: this.state.formNumber.id,
        agentuserids,
      };
      ProviderGroupService.createGroup(payload)
        .then(result => (result.data && result.data.success) ? onOk('providergroup.success.created') : onFail(result, 'providergroup.error.creating'))
        .catch(err => onError(err, 'providergroup.error.creating'));
    }
  }

  copyToClipboard = (value) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    this.setState({
      openNotification: true, notificationVariant: 'success',
      notificationMessage: this.props.intl.formatMessage({ id: 'providergroup.copied' }),
    });
  }

  renderStatus = (group) => {
    const intl = this.props.intl;
    if (group.operationStatus === OP_CONFIRMED) {
      return <Chip size="small" label={intl.formatMessage({ id: 'providergroup.status.confirmed' })} sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />;
    }
    if (group.operationStatus === OP_FAILED) {
      return (
        <Tooltip title={group.errorMessage || group.errorCode || ''}>
          <Chip size="small" label={intl.formatMessage({ id: 'providergroup.status.failed' })} sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600 }} />
        </Tooltip>
      );
    }
    if (group.operationStatus === OP_PENDING) {
      return <Chip size="small" label={intl.formatMessage({ id: 'providergroup.status.pending' })} sx={{ bgcolor: '#fff8e1', color: '#f57f17', fontWeight: 600 }} />;
    }
    return <Chip size="small" label={intl.formatMessage({ id: 'providergroup.status.unknown' })} sx={{ bgcolor: '#eceff1', color: '#607d8b' }} />;
  }

  closeNotification() {
    this.setState({ openNotification: false });
  }

  render() {
    const { classes, headerMenu, actionsHeaderMenu } = this.props;
    const intl = this.props.intl;
    const { groups, loading, actionLoading } = this.state;

    return (
      <div className={classes.root} style={{ paddingLeft: 0, marginLeft: 0 }}>
        <HeaderMenu />
        <main className={clsx(classes.content, { [classes.contentShift]: headerMenu.open }, classes.actionsContent, { [classes.actionscontentShift]: actionsHeaderMenu.open })}>
          <div className={classes.drawerHeader} />
          <Container component='main' maxWidth='xl' style={{ maxWidth: '100%', paddingLeft: 0, marginLeft: 0 }}>
            <CssBaseline />
            <div style={pageStyles.contentWrapper}>
              <h1 style={pageStyles.heading}>{intl.formatMessage({ id: 'providergroup.title' })}</h1>
              <p style={pageStyles.subheading}>{intl.formatMessage({ id: 'providergroup.subtitle' })}</p>

              <div style={pageStyles.toolbar}>
                <Button variant="contained" startIcon={<GroupsIcon />} onClick={this.openModal}
                  sx={{ bgcolor: '#8BC34A', '&:hover': { bgcolor: '#7cb342' } }}>
                  {intl.formatMessage({ id: 'providergroup.button.create' })}
                </Button>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => this.loadData(true)}>
                  {intl.formatMessage({ id: 'providergroup.button.refresh' })}
                </Button>
              </div>

              {loading ? (
                <TableContainer component={Paper} sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', boxShadow: 'none' }}>
                  <Table size="small">
                    <TableBody>
                      {[1, 2, 3].map(i => (
                        <TableRow key={i}>
                          <TableCell colSpan={6}><Skeleton variant="text" height={32} animation="wave" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : groups.length === 0 ? (
                <div style={pageStyles.emptyCard}>
                  <GroupsIcon style={{ fontSize: 56, color: '#bdbdbd' }} />
                  <div style={pageStyles.emptyTitle}>{intl.formatMessage({ id: 'providergroup.empty.title' })}</div>
                  <div style={pageStyles.emptyDesc}>{intl.formatMessage({ id: 'providergroup.empty.description' })}</div>
                  <Button variant="contained" startIcon={<GroupsIcon />} onClick={this.openModal}
                    sx={{ bgcolor: '#8BC34A', '&:hover': { bgcolor: '#7cb342' } }}>
                    {intl.formatMessage({ id: 'providergroup.button.create' })}
                  </Button>
                </div>
              ) : (
                <TableContainer component={Paper} sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', boxShadow: 'none' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell sx={{ fontWeight: 600 }}>{intl.formatMessage({ id: 'providergroup.table.name' })}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{intl.formatMessage({ id: 'providergroup.table.status' })}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{intl.formatMessage({ id: 'providergroup.table.groupid' })}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{intl.formatMessage({ id: 'providergroup.table.invitelink' })}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">{intl.formatMessage({ id: 'providergroup.table.agents' })}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{intl.formatMessage({ id: 'providergroup.table.created' })}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">{intl.formatMessage({ id: 'providergroup.table.actions' })}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groups.map(g => (
                        <TableRow key={g.chatId} hover sx={{ cursor: 'pointer' }} onClick={() => this.openEdit(g)}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.groupName}</Typography>
                            {g.groupDescription ? <Typography variant="caption" color="text.secondary">{g.groupDescription}</Typography> : null}
                          </TableCell>
                          <TableCell>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              {this.renderStatus(g)}
                              {/* Falhou -> icone de erro; nao propaga o clique da linha (abre o modal de erro). */}
                              {g.operationStatus === OP_FAILED ? (
                                <Tooltip title={intl.formatMessage({ id: 'providergroup.error.viewdetails' })}>
                                  <IconButton size="small" color="error"
                                    onClick={(e) => { e.stopPropagation(); this.openErrorModal(g); }}>
                                    <ErrorOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </span>
                          </TableCell>
                          <TableCell>
                            {g.groupId ? (
                              <span style={pageStyles.mono}>
                                {g.groupId}
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); this.copyToClipboard(g.groupId); }}><ContentCopyIcon fontSize="inherit" /></IconButton>
                              </span>
                            ) : <span style={{ color: '#bdbdbd' }}>—</span>}
                          </TableCell>
                          <TableCell>
                            {g.inviteLink ? (
                              <span style={pageStyles.link}>
                                {g.inviteLink}
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); this.copyToClipboard(g.inviteLink); }}><ContentCopyIcon fontSize="inherit" /></IconButton>
                              </span>
                            ) : <span style={{ color: '#bdbdbd' }}>—</span>}
                          </TableCell>
                          <TableCell align="center">{g.agentCount}</TableCell>
                          <TableCell>{g.createdAt ? new Date(g.createdAt).toLocaleString() : '—'}</TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <IconButton size="small" onClick={(e) => this.openRowMenu(e, g)}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          </Container>
        </main>

        {/* Menu 3-pontinhos por linha (extensivel: futuras acoes como excluir/sincronizar entram aqui). */}
        <Menu anchorEl={this.state.menuAnchor} open={Boolean(this.state.menuAnchor)} onClose={this.closeRowMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <MenuItem onClick={() => { const g = this.state.menuGroup; this.closeRowMenu(); this.openEdit(g); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{intl.formatMessage({ id: 'providergroup.button.edit' })}</ListItemText>
          </MenuItem>
          {/* Reenviar so quando NAO confirmado (sem group_id) — confirmado geraria grupo duplicado. */}
          {this.state.menuGroup && !this.state.menuGroup.groupId ? (
            <MenuItem disabled={this.state.actionLoading}
              onClick={() => { const g = this.state.menuGroup; this.closeRowMenu(); this.handleRetry(g); }}>
              <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{intl.formatMessage({ id: 'providergroup.menu.retry' })}</ListItemText>
            </MenuItem>
          ) : null}
          {/* Resetar link so quando JA confirmado (tem group_id) — antes disso nao existe link na Meta. */}
          {this.state.menuGroup && this.state.menuGroup.groupId ? (
            <MenuItem disabled={this.state.actionLoading}
              onClick={() => { const g = this.state.menuGroup; this.closeRowMenu(); this.handleResetInvite(g); }}>
              <ListItemIcon><AutorenewIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{intl.formatMessage({ id: 'providergroup.menu.resetinvite' })}</ListItemText>
            </MenuItem>
          ) : null}
        </Menu>

        {/* Modal de ERRO — op.error_message + rastro do fluxo (tb_integration_event_trace) */}
        <Dialog open={this.state.errorModalOpen} onClose={this.closeErrorModal} fullWidth maxWidth="md"
          PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 600 }}>{intl.formatMessage({ id: 'providergroup.error.modal.title' })}</DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '16px 20px' }}>
              {/* Erro da operacao (tb_2talk_provider_group_operation) */}
              {this.state.errorGroup ? (
                <div style={{ background: '#fff5f5', border: '1px solid #ffcdd2', borderRadius: 8, padding: 14, marginBottom: 18 }}>
                  <Typography variant="subtitle2" sx={{ color: '#c62828', fontWeight: 700, mb: 0.5 }}>
                    {this.state.errorGroup.errorCode || intl.formatMessage({ id: 'providergroup.status.failed' })}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                    {this.state.errorGroup.errorMessage || '—'}
                  </Typography>
                </div>
              ) : null}

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {intl.formatMessage({ id: 'providergroup.error.trace.title' })}
              </Typography>

              {this.state.loadingTrace ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><CircularProgress size={22} /></div>
              ) : this.state.trace.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{intl.formatMessage({ id: 'providergroup.error.trace.empty' })}</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{intl.formatMessage({ id: 'providergroup.error.trace.when' })}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{intl.formatMessage({ id: 'providergroup.error.trace.handler' })}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{intl.formatMessage({ id: 'providergroup.error.trace.status' })}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{intl.formatMessage({ id: 'providergroup.error.trace.error' })}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {this.state.trace.map((t, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{t.createdAt ? new Date(t.createdAt).toLocaleString() : '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{(t.handlerName || '').replace('IntegrationEventHandler', '')}</TableCell>
                        <TableCell>
                          <Chip size="small" label={t.status}
                            sx={{ fontSize: 11, bgcolor: t.status === 'failed' ? '#ffebee' : (t.status === 'completed' ? '#e8f5e9' : '#eceff1'),
                                  color: t.status === 'failed' ? '#c62828' : (t.status === 'completed' ? '#2e7d32' : '#607d8b') }} />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: '#b71c1c', maxWidth: 380, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {t.errorMessage || ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={this.closeErrorModal}>{intl.formatMessage({ id: 'providergroup.button.cancel' })}</Button>
          </DialogActions>
        </Dialog>

        {/* Modal de criacao — rolagem via div PURO com style inline (regra do projeto) */}
        <Dialog open={this.state.modalOpen} onClose={this.closeModal} fullWidth maxWidth="sm"
          PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 600 }}>
            {intl.formatMessage({ id: this.state.editMode ? 'providergroup.modal.title.edit' : 'providergroup.modal.title' })}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px 20px' }}>
              {/* Cascata (provedor/canal/numero) SO na criacao — na edicao o grupo ja esta atrelado. */}
              {!this.state.editMode ? (
              <React.Fragment>
              <Autocomplete
                options={this.state.providers}
                getOptionLabel={(o) => o.name || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={this.state.formProvider}
                onChange={(e, value) => this.handleProviderChange(value)}
                renderInput={(params) => (
                  <TextField {...params} required
                    label={intl.formatMessage({ id: 'providergroup.field.provider' })}
                    InputLabelProps={{ shrink: true }} />
                )}
                sx={{ mb: 2 }}
              />
              <Autocomplete
                options={this.state.channels}
                loading={this.state.loadingChannels}
                disabled={!this.state.formProvider || this.state.loadingChannels}
                getOptionLabel={(o) => o.name || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={this.state.formChannel}
                onChange={(e, value) => this.handleChannelChange(value)}
                noOptionsText={intl.formatMessage({ id: 'providergroup.field.channel.empty' })}
                renderInput={(params) => (
                  <TextField {...params} required
                    label={intl.formatMessage({ id: 'providergroup.field.channel' })}
                    InputLabelProps={{ shrink: true }}
                    helperText={this.state.formProvider && !this.state.loadingChannels && this.state.channels.length === 0
                      ? intl.formatMessage({ id: 'providergroup.field.channel.empty' }) : ''}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {this.state.loadingChannels ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }} />
                )}
                sx={{ mb: 2 }}
              />
              <Autocomplete
                options={this.state.numbers}
                loading={this.state.loadingNumbers}
                disabled={!this.state.formChannel || this.state.loadingNumbers}
                getOptionLabel={(o) => `${o.phoneNumber || o.phoneNumberId || ''}${o.accountName ? ' — ' + o.accountName : ''}`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={this.state.formNumber}
                onChange={(e, value) => this.setState({ formNumber: value })}
                noOptionsText={intl.formatMessage({ id: 'providergroup.field.number.empty' })}
                renderInput={(params) => (
                  <TextField {...params} required
                    label={intl.formatMessage({ id: 'providergroup.field.number' })}
                    InputLabelProps={{ shrink: true }}
                    helperText={this.state.formChannel && !this.state.loadingNumbers && this.state.numbers.length === 0
                      ? intl.formatMessage({ id: 'providergroup.field.number.empty' }) : ''}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {this.state.loadingNumbers ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }} />
                )}
                sx={{ mb: 2 }}
              />
              </React.Fragment>
              ) : null}
              <TextField
                label={intl.formatMessage({ id: 'providergroup.field.name' })}
                value={this.state.formName}
                onChange={e => this.setState({ formName: e.target.value })}
                inputProps={{ maxLength: 128 }}
                InputLabelProps={{ shrink: true }}
                fullWidth required sx={{ mb: 2 }}
              />
              <TextField
                label={intl.formatMessage({ id: 'providergroup.field.description' })}
                value={this.state.formDescription}
                onChange={e => this.setState({ formDescription: e.target.value })}
                inputProps={{ maxLength: 2048 }}
                InputLabelProps={{ shrink: true }}
                fullWidth multiline rows={3} sx={{ mb: 2 }}
              />
              {/* Modo de entrada: travado quando o grupo ja existe na Meta (nao e alteravel pos-criacao). */}
              <TextField
                select
                label={intl.formatMessage({ id: 'providergroup.field.joinmode' })}
                value={this.state.formJoinMode}
                onChange={e => this.setState({ formJoinMode: e.target.value })}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                disabled={this.state.editConfirmed}
                helperText={this.state.editConfirmed ? intl.formatMessage({ id: 'providergroup.field.joinmode.locked' }) : ''}
                fullWidth sx={{ mb: 2 }}
              >
                <option value="auto_approve">{intl.formatMessage({ id: 'providergroup.field.joinmode.auto' })}</option>
                <option value="approval_required">{intl.formatMessage({ id: 'providergroup.field.joinmode.approval' })}</option>
              </TextField>
              <Autocomplete
                multiple
                options={this.state.users}
                getOptionLabel={(o) => o.name || o.nome || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={this.state.formAgents}
                onChange={(e, value) => this.setState({ formAgents: value })}
                renderInput={(params) => (
                  <TextField {...params} label={intl.formatMessage({ id: 'providergroup.field.agents' })} InputLabelProps={{ shrink: true }} />
                )}
              />
            </div>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={this.closeModal} disabled={actionLoading}>
              {intl.formatMessage({ id: 'providergroup.button.cancel' })}
            </Button>
            <Button variant="contained" onClick={this.handleCreate} disabled={actionLoading}
              sx={{ bgcolor: '#8BC34A', '&:hover': { bgcolor: '#7cb342' } }}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}>
              {intl.formatMessage({ id: 'providergroup.button.save' })}
            </Button>
          </DialogActions>
        </Dialog>

        <CustomizedSnackbars
          variant={this.state.notificationVariant}
          message={this.state.notificationMessage}
          isOpen={this.state.openNotification}
          toClose={this.closeNotification}
        />
      </div>
    );
  }
}

ProviderGroupList.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  actionsHeaderMenu: state.actionsHeaderMenu,
  headerMenu: state.headerMenu,
  userSession: state.userSession,
});
const mapDispatchToProps = dispatch => bindActionCreators(componentActions, dispatch);

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withStyles(Styles)(ProviderGroupList)));
